import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { Locale, VideoKey } from "../shared/dto";
import { intlLanguage } from "./date-format";

// M6 staff surfaces for the gated videos (D6, David: "abysme věděli, že tím
// prošel"): per-client watch state on the client detail plus the admin's
// global default urls (one video for everyone without a per-client override).

interface StaffVideoRow {
  key: VideoKey;
  configured: null | { url: string; title: string; source: "client" | "global"; unlockAtSeconds: number | null };
  progress: null | {
    positionSeconds: number;
    maxWatchedSeconds: number;
    durationSeconds: number | null;
    watchedPct: number;
    completedAt: string | null;
    updatedAt: string;
    unlockReady: boolean;
  };
}

function formatDate(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "short", timeStyle: "short" }).format(date)
    : value;
}

export function StaffVideosCard(props: {
  clientId: string;
  locale: Locale;
  t: (key: string) => string;
  /** F81: a step workspace shows only its own video; the overview shows both. */
  only?: VideoKey;
  /**
   * 🔴 F138 kolečko 3: on the client's OVERVIEW an empty dashed box saying
   * "Klient zatím žádné video nemá" is a premium position spent on nothing -
   * and nothing is lost by silence, because configuring videos lives in Servis
   * and a client with no video is already a service row in the partner queue
   * (F81's `video_missing`). The STEP workspace does not pass this: there the
   * video IS the step, so the empty state is the honest answer to "why is this
   * step quiet" and must keep its place.
   */
  hideWhenEmpty?: boolean;
}) {
  const [rows, setRows] = useState<StaffVideoRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const response = await fetch(`/api/staff/clients/${encodeURIComponent(props.clientId)}/videos`, { credentials: "include" });
      if (!response.ok) throw new Error("staff_videos_failed");
      const body = (await response.json()) as { videos: StaffVideoRow[] };
      setRows(body.videos);
    } catch {
      setFailed(true);
    }
  }, [props.clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const t = props.t;
  const scopedRows = props.only ? rows?.filter((row) => row.key === props.only) ?? null : rows;
  const configuredRows = scopedRows?.filter((row) => row.configured || row.progress) ?? [];
  // Silence only once the read has ANSWERED: hiding while `rows === null` would
  // make the card appear a beat after the page settles, and a block that
  // arrives late reads as a page still loading. A failed read still speaks.
  if (props.hideWhenEmpty && !failed && rows !== null && configuredRows.length === 0) return null;
  return (
    <article className="staff-card" data-staff-videos="true">
      <h3>{t("staffConsole.videos.title")}</h3>
      {failed && <p className="message error-message" role="alert">{t("common.error")}</p>}
      {!failed && rows === null && <p className="staff-empty">{t("common.loading")}</p>}
      {!failed && rows !== null && configuredRows.length === 0 && (
        <p className="staff-empty">{t("staffConsole.videos.none")}</p>
      )}
      {!failed && configuredRows.map((row) => {
        const completed = Boolean(row.progress?.completedAt);
        const pct = row.progress?.watchedPct ?? 0;
        return (
          <div className="staff-video-row" key={row.key}>
            <div className="staff-video-head">
              <strong>{t(`staffConsole.videos.kind.${row.key}`)}</strong>
              <span>
                {completed
                  ? `${t("staffConsole.videos.completed")} ${formatDate(row.progress!.completedAt!, props.locale)}`
                  : row.progress
                    ? `${t("staffConsole.videos.watched")} ${pct} %`
                    : t("staffConsole.videos.notStarted")}
              </span>
            </div>
            <div className="staff-video-track" role="img" aria-label={`${t("staffConsole.videos.watched")} ${completed ? 100 : pct} %`}>
              <div className={`staff-video-fill${completed ? " done" : ""}`} style={{ width: `${completed ? 100 : pct}%` }} />
            </div>
            {/* F81: the fact the partner actually needs - did the video open
                the client's next step yet. */}
            {row.progress?.unlockReady && (
              <p className="staff-video-note" data-staff-video-unlocked="true">{t("staffConsole.videos.unlockReady")}</p>
            )}
            {row.configured
              ? <p className="staff-video-note">{row.configured.title} · {t(`staffConsole.videos.source.${row.configured.source}`)}</p>
              : <p className="staff-video-note">{t("staffConsole.videos.unconfiguredHistory")}</p>}
          </div>
        );
      })}
    </article>
  );
}

// The global default videos moved to the Servis settings area
// (src/staff/AdminSettingsContent.tsx): the form belongs next to the overview
// of who actually sees the video, so a save can answer with its reach.
